import type { CalendarDay, Instant } from '../validators/temporal.validator.js'

/** `yyyy-MM-dd` — a calendar day, which is not a moment in time. */
const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/

/**
 * Shared date formatter, so the API and web app format the same value the same
 * way. Formatting, normalization and general utilities live in
 * `lib/src/helpers`.
 *
 * A **calendar day** is formatted in UTC, because that is the only zone it was
 * ever in: `new Date('2027-05-14')` is UTC midnight, and formatting it in the
 * viewer's zone shows "May 13" to everybody west of Greenwich. A real instant
 * keeps the viewer's zone, which is what a timestamp should do.
 *
 * That distinction is the same one `docs/event-scheduling.md` is about: a day
 * anchor and an instant are different things, and one formatter that treats
 * them alike gets one of them wrong.
 *
 * The parameter names the two it accepts, which leaves out the third: a
 * `WallClock` is 22:00 *at the venue* and has no business being formatted here
 * at all — `fmt12` reads it. Passing one used to compile.
 */
export function formatDate(
  value: CalendarDay | Instant | Date,
  locale = 'en-US',
): string {
  const isCalendarDay = typeof value === 'string' && CALENDAR_DAY.test(value)
  const date = typeof value === 'string' ? new Date(value) : value
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(isCalendarDay ? { timeZone: 'UTC' } : {}),
  })
}

/**
 * A name's possessive, as American house style writes it for names: an
 * apostrophe alone after a final s, "’s" otherwise. "River Garden Weddings &
 * Events’s vendors" read wrong on the couple's own event page, and every
 * "Events", "Farms" and "Weddings" a venue is called has the same ending.
 */
export function possessive(name: string): string {
  const trimmed = name.trimEnd()
  return /s$/i.test(trimmed) ? `${trimmed}’` : `${trimmed}’s`
}

/**
 * Dollars from cents, the one way the app and its emails write money: whole
 * dollars bare ("$30"), anything else to the cent ("$12.50").
 */
export function dollarsFromCents(cents: number): string {
  const whole = cents % 100 === 0
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 })}`
}
